import { Global } from "@emotion/core";
import { ThemeKey, commonTokens } from "@smartsheet/design-tokens";
import { HTMLAttributes, useEffect, useLayoutEffect, useRef, useState } from "react";
import React from "react";
import { BODY_MARKER_ATTRIBUTE } from "./ThemeMarkerAttribute";
import { createTokenInjector } from "./createTokenInjector";

const BACKGROUND_COLOR_TRANSITION_MILLISECONDS = 200;

const { getToken, Injector } = createTokenInjector({
    tokens: commonTokens,
    supportedThemes: ["lodestar-light", "ursa-light"],
    namespace: "com",
});

export interface CommonTokenInjectorProps extends HTMLAttributes<HTMLElement> {
    themeKey: ThemeKey;
}

export const CommonTokenInjector = ({ themeKey }: CommonTokenInjectorProps) => {
    const [_actualThemeKey, setActualThemeKey] = useState(themeKey);
    const [applyBackgroundColorTransition, setApplyBackgroundColorTransition] = useState(false);

    // Users should get the right theme immediately. This helps w/ tests and the initial paint.
    const firstMountRef = useRef(true);
    if (firstMountRef.current) {
        document.body.setAttribute(BODY_MARKER_ATTRIBUTE, themeKey);
        firstMountRef.current = false;
    }

    useLayoutEffect(() => {
        let animationTimeoutId = 0;
        setApplyBackgroundColorTransition(true);
        // We want to apply the CSS change after a tick to ensure all the React stuff has settled
        // first.
        const rafId = requestAnimationFrame(() => {
            setActualThemeKey(themeKey);
            animationTimeoutId = window.setTimeout(() => {
                setApplyBackgroundColorTransition(false);
            }, BACKGROUND_COLOR_TRANSITION_MILLISECONDS);

            document.body.setAttribute(BODY_MARKER_ATTRIBUTE, themeKey);
        });
        return () => {
            cancelAnimationFrame(rafId);
            clearTimeout(animationTimeoutId);
        };
    }, [themeKey]);

    // When this component is unmounted, we should remove the attribute to clean up.
    useEffect(
        () => () => {
            document.body.removeAttribute(BODY_MARKER_ATTRIBUTE);
        },
        [themeKey]
    );

    return (
        <>
            <Injector themeKey={themeKey} />
            <Global
                styles={{
                    // This should only be added when the themeKey is transitioning or
                    // it affects everything else pretty badly.
                    "*, *:before, *.after": applyBackgroundColorTransition
                        ? {
                              transition: `
                    background-color ${BACKGROUND_COLOR_TRANSITION_MILLISECONDS}ms linear,
                    border-color ${BACKGROUND_COLOR_TRANSITION_MILLISECONDS}ms linear,
                    color ${BACKGROUND_COLOR_TRANSITION_MILLISECONDS}ms linear
                    `,
                          }
                        : {},
                }}
            />
        </>
    );
};

export const color = getToken;
